import React, { useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { pathKeys } from "../../constants";
import types from "../../constants/types";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { dashboard as selectDashboard } from "views/Home/HomeSelectors";
import { selectShare as selectAppShare } from "views/App/AppSelectors";
import PricingModal from "components/Modals/PricingModal";
import VersionModal from "components/VersionModal/VersionModal";

const useStyles = makeStyles((theme) => ({
  header: {
    flexGrow: 1,
    padding: 0,
    position: "fixed",
    top: 0,
    right: 0,
    zIndex: 90,
    width: "calc(100% - 232px)",
    border: "none",
    transition: "width 0.2s ease",
  },
  headerDehaze: {
    flexGrow: 1,
    padding: 0,
    position: "fixed",
    top: 0,
    right: 0,
    zIndex: 90,
    width: "calc(100% - 68px)",
    border: "none",
    transition: "width 0.2s ease",
  },
  headerContent: {
    height: 60,
    background: "#ffffff",
    borderBottom: "1px solid #E5E7EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 24px",
    gap: 14,
    fontFamily: "'Be Vietnam Pro', sans-serif",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },
  accountPills: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      display: "none"
    }
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 500,
    border: "1px solid #E5E7EB",
    background: "#F9FAFB",
    color: "#6B7280",
    whiteSpace: "nowrap",
  },
  pillGreen: {
    background: "#F0FDF4",
    borderColor: "#86EFAC",
    color: "#16A34A",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexShrink: 0,
  },
  btnUpgrade: {
    background: "#F59E0B",
    color: "#fff",
    border: "none",
    padding: "7px 14px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    textTransform: "uppercase",
    transition: "transform 0.12s",
    "&:hover": {
      filter: "brightness(0.95)",
      transform: "translateY(-1px)",
    }
  },
  userChip: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "5px 10px",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    cursor: "pointer",
    background: "#ffffff",
    whiteSpace: "nowrap",
    transition: "background 0.12s",
    "&:hover": {
      background: "#F9FAFB"
    }
  },
  userAvatar: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: "#DCFCE7",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 600,
    color: "#16A34A",
    flexShrink: 0,
  },
  userName: {
    fontSize: 12,
    fontWeight: 600,
    color: "#111827",
  },
  chevron: {
    width: 12,
    height: 12,
    stroke: "#9CA3AF",
    strokeWidth: 2,
  },
  wrapVersion: {
    padding: "0px 15px",
    "& h4": {
      marginBottom: 0,
      padding: 0,
      color: "#05AE34"
    },
    "& h6": {
      marginTop: 5,
      fontSize: 14,
      color: "red"
    }
  },
  /* ─── Custom dropdown (MessengerButton style) ─── */
  dropdownOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 199,
  },
  dropdown: {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    zIndex: 200,
    width: 240,
    background: "#ffffff",
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    boxShadow: "0 16px 40px rgba(0,0,0,0.14)",
    overflow: "hidden",
    animation: "$popIn 0.15s ease",
  },
  dropdownHeader: {
    padding: "13px 16px",
    background: "linear-gradient(120deg, #14532D 0%, #16A34A 60%, #22C55E 100%)",
    color: "#fff",
  },
  dropdownTitle: {
    fontSize: 13,
    fontWeight: 700,
    fontFamily: "'Be Vietnam Pro', sans-serif",
  },
  dropdownSub: {
    fontSize: 11,
    opacity: 0.85,
    marginTop: 2,
    fontFamily: "'Be Vietnam Pro', sans-serif",
  },
  dropdownList: {
    padding: 6,
  },
  dropdownItem: {
    display: "flex",
    alignItems: "center",
    gap: 11,
    padding: "9px 10px",
    borderRadius: 8,
    cursor: "pointer",
    background: "transparent",
    border: "none",
    width: "100%",
    textAlign: "left",
    fontFamily: "'Be Vietnam Pro', sans-serif",
    transition: "background 0.12s",
    "&:hover": {
      background: "#F3F4F6",
    },
  },
  dropdownIcon: {
    width: 34,
    height: 34,
    borderRadius: 8,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dropdownLabel: {
    fontSize: 12.5,
    fontWeight: 600,
    color: "#111827",
  },
  "@keyframes popIn": {
    "0%": { opacity: 0, transform: "translateY(-6px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
}));

export default function Header(props) {
  const { getMenu, onMenu, onLogout, user, onDehaze, dehaze } = props;
  const classes = useStyles();
  const history = useHistory();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isPopupVersion, setIsPopupVersion] = React.useState(false);
  const { t, i18n } = useTranslation();
  const share = useSelector(selectAppShare);
  const dashboard = useSelector(selectDashboard);
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);
  const userChipRef = useRef(null);

  // Tính tin nhắn còn lại từ dashboard.info
  const accountInfo = dashboard?.info || {}
  const totalMes = parseInt(accountInfo.totalMes || '0', 10)
  const accountType = accountInfo.type || ''
  const isUnlimitedMsgs = totalMes > 1000000000
  const isLifetimePackage = accountType === '12'
  const remainMesLabel = isUnlimitedMsgs
    ? t('header_stats.unlimited_messages')
    : t('header_stats.messages_left', { count: (totalMes).toLocaleString('vi-VN') })
  const packageLabel = isLifetimePackage ? t('header_stats.lifetime_package') : (accountType ? t('header_stats.package', { type: accountType }) : t('header_stats.basic_package'))

  const [openPricing, setOpenPricing] = useState(false);

  const handleClick = () => {
    setDropdownOpen((v) => !v);
  };

  const handleClose = () => {
    setDropdownOpen(false);
  };

  const handleSettingAccount = () => {
    history.push(pathKeys.SETTING_ACCOUNT);
    onMenu("Quản lý tài khoản");
    handleClose();
  };

  const handleSynchronize = () => {
    history.push(pathKeys.SYNCHRONIZE);
    onMenu("Đồng bộ");
    handleClose();
  };

  const handleLogOut = () => {
    localStorage.removeItem("alertGiftBox");
    onLogout();
    handleClose();
  };

  const handleClickPay = () => {
    setOpenPricing(true);
  };

  const handleCloseVersion = () => {
    setIsPopupVersion(false);
  };

  const handleVersion = () => {
    setIsPopupVersion(true);
    handleClose();
  };

  const lang = i18n?.language?.substring(0, 2);
  let versionContent = share?.version?.content;
  if (lang === "en" && share?.version?.contentEn) {
    versionContent = share?.version?.contentEn;
  } else if (lang === "kh" && share?.version?.contentKh) {
    versionContent = share?.version?.contentKh;
  }

  const getInitials = (name) => {
    if (!name) return "U";
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className={cn(dehaze ? classes.headerDehaze : classes.header)}>
      <div className={classes.headerContent}>
        <div className={classes.headerLeft}>
          <div className={classes.accountPills}>
            <span className={cn(classes.pill, classes.pillGreen)}>{remainMesLabel}</span>
            <span className={cn(classes.pill, classes.pillGreen)}>{packageLabel}</span>
          </div>
        </div>

        <div className={classes.headerRight}>
          <button id="upgrade" className={classes.btnUpgrade} onClick={handleClickPay}>
            {t("common.btn.upgrade")}
          </button>
          
          <div style={{ position: "relative" }} ref={userChipRef}>
            <div className={classes.userChip} onClick={handleClick}>
              {user?.image ? (
                <img src={user.image} alt="avatar" style={{width: 26, height: 26, borderRadius: '50%'}} />
              ) : (
                <div className={classes.userAvatar}>
                  {getInitials(user?.name)}
                </div>
              )}
              <span className={classes.userName}>{user?.name || "User"}</span>
              <svg className={classes.chevron} viewBox="0 0 12 12" fill="none">
                <path d="M3 5l3 3 3-3" />
              </svg>
            </div>

            {dropdownOpen && (
              <>
                <div className={classes.dropdownOverlay} onClick={handleClose} />
                <div className={classes.dropdown} ref={dropdownRef}>
                  {/* Menu items */}
                  <div className={classes.dropdownList}>
                    {/* Quản lý tài khoản */}
                    <button className={classes.dropdownItem} onClick={handleSettingAccount}>
                      <div className={classes.dropdownIcon} style={{ background: "#F0FDF4" }}>
                        <svg viewBox="0 0 16 16" style={{ width: 16, height: 16, strokeWidth: 1.8, fill: "none", stroke: "#16A34A" }}>
                          <circle cx="8" cy="5.5" r="3" />
                          <path d="M2.5 14c0-3 2.5-4.5 5.5-4.5s5.5 1.5 5.5 4.5" />
                        </svg>
                      </div>
                      <span className={classes.dropdownLabel}>{t("menu.account")}</span>
                    </button>

                    {/* Thông tin phiên bản */}
                    <button className={classes.dropdownItem} onClick={handleVersion}>
                      <div className={classes.dropdownIcon} style={{ background: "#EFF6FF" }}>
                        <svg viewBox="0 0 16 16" style={{ width: 16, height: 16, strokeWidth: 1.8, fill: "none", stroke: "#1D4ED8" }}>
                          <circle cx="8" cy="8" r="6" />
                          <path d="M8 5v3" />
                          <circle cx="8" cy="11" r="0.5" fill="#1D4ED8" stroke="none" />
                        </svg>
                      </div>
                      <span className={classes.dropdownLabel}>{t("menu.version")}</span>
                    </button>

                    {/* Đồng bộ */}
                    <button className={classes.dropdownItem} onClick={handleSynchronize}>
                      <div className={classes.dropdownIcon} style={{ background: "#FFFBEB" }}>
                        <svg viewBox="0 0 16 16" style={{ width: 16, height: 16, strokeWidth: 1.8, fill: "none", stroke: "#92400E" }}>
                          <path d="M2.5 8a5.5 5.5 0 019.5-3.5M13.5 8a5.5 5.5 0 01-9.5 3.5" />
                          <path d="M12 2v3h-3M4 11v3h3" />
                        </svg>
                      </div>
                      <span className={classes.dropdownLabel}>{t("menu.synchronize")}</span>
                    </button>

                    {/* Đăng xuất */}
                    <button className={classes.dropdownItem} onClick={handleLogOut}>
                      <div className={classes.dropdownIcon} style={{ background: "#FEF2F2" }}>
                        <svg viewBox="0 0 16 16" style={{ width: 16, height: 16, strokeWidth: 1.8, fill: "none", stroke: "#DC2626" }}>
                          <path d="M6 2H3.5A1.5 1.5 0 002 3.5v9A1.5 1.5 0 003.5 14H6" />
                          <path d="M10.5 11.5L14 8l-3.5-3.5M5.5 8h8.5" />
                        </svg>
                      </div>
                      <span className={classes.dropdownLabel}>{t("menu.logout")}</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <VersionModal
        isOpen={isPopupVersion}
        onClose={handleCloseVersion}
        version={share?.version}
        lang={lang}
      />
      <PricingModal 
        open={openPricing} 
        onClose={() => setOpenPricing(false)} 
        user={user} 
        i18n={i18n} 
      />
    </div>
  );
}
